import React, {useState} from "react";
import {
  Eye,
  Redo2,
  RefreshCw,
  Rocket,
  TextCursorInput,
  Undo2,
} from "lucide-react";
import {Button} from "@/components/ui/Button";
import Checklist from "./Checklist";
import {Tabs, TabsList, TabsTrigger} from "@/components/ui/tabs";
import {useSave} from "@/contexts/SaveContext";
import AutoSave from "./AutoSave";
import {FlowMode, SavingState} from "@/types/node";
import {Separator} from "@/components/ui/separator";
import {useReactFlow} from "@xyflow/react";
import {DeployModal} from "./Modals/DeployModal";
import {ResetModal} from "./Modals/ResetModal";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {FlowBuilderSettings} from "@/config/flowBuilderSettings";
import {SwitchModeModal} from "./Modals/SwitchModeModal";
import {cn} from "@/lib/utils";
interface TopbarProps {
  setShowPrompt: (showPrompt: boolean) => void;
  undoRedoDetails: {
    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
    resetUndoRedo: () => void;
  };
  disabled: boolean;
}

const Topbar: React.FC<TopbarProps> = ({
  setShowPrompt,
  undoRedoDetails,
  disabled,
}) => {
  const {
    flowMode,
    setFlowMode,
    handleSave,
    flowData,
    setFlowData,
    savingState,
  } = useSave();
  const {getNodes, getEdges} = useReactFlow();
  const nodes = getNodes();
  const edges = getEdges();
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showSwitchModeModal, setShowSwitchModeModal] = useState(false);

  const {undo, redo, canUndo, canRedo} = undoRedoDetails;

  const isLiveMode = flowMode === FlowMode.LIVE;

  const handlePublish = () => {
    setFlowData({
      test: {
        version: flowData.test.version,
        nodes: nodes,
        edges: edges,
      },
      live: {
        version: flowData.test.version,
        nodes: nodes,
        edges: edges,
      },
    });
    handleSave();
  };

  const handleReset = () => {
    setFlowData({...flowData, test: flowData.live});
    handleSave();
  };

  const handleSwitchMode = () => {
    handleSave(() => {
      FlowBuilderSettings.onModeChange && FlowBuilderSettings.onModeChange();
      setShowSwitchModeModal(false);
    }, true);
  };

  const TopbarContent = (
    <div
      className={cn(
        "flex justify-between items-center px-5 py-2.5 border-b bg-background",
        disabled && "opacity-50 pointer-events-none",
      )}
    >
      <div className="flex gap-3 flex-1">
        <div className="flex flex-col">
          <h2 className="text-sm font-medium">Flow Designer</h2>
          <p className="text-xs text-secondary text-nowrap">
            Virtual AI Assistant
          </p>
        </div>
        {FlowBuilderSettings.onModeChange && (
          <Button
            variant="outline"
            className="[&_svg]:size-5"
            onClick={() => {
              setShowSwitchModeModal(true);
            }}
          >
            <TextCursorInput className="text-primary-accent" />
            Switch to Prompt
          </Button>
        )}
      </div>
      <div className="flex gap-3">
        <Tooltip delayDuration={0} disableHoverableContent={true}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="[&_svg]:size-5"
              onClick={() => {
                handleSave();
                undo();
              }}
              disabled={!canUndo || isLiveMode}
            >
              <Undo2 />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" sideOffset={10}>
            Undo
          </TooltipContent>
        </Tooltip>

        <Tooltip delayDuration={0} disableHoverableContent={true}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="[&_svg]:size-5"
              onClick={() => {
                handleSave();
                redo();
              }}
              disabled={!canRedo || isLiveMode}
            >
              <Redo2 />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" sideOffset={10}>
            Redo
          </TooltipContent>
        </Tooltip>
        {!isLiveMode && (
          <Tooltip delayDuration={0} disableHoverableContent={true}>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="[&_svg]:size-5"
                onClick={() => setShowResetModal(true)}
              >
                <RefreshCw />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={10}>
              Reset
            </TooltipContent>
          </Tooltip>
        )}
        <Tooltip delayDuration={0} disableHoverableContent={true}>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="[&_svg]:size-5"
              onClick={() => setShowPrompt(true)}
            >
              <Eye />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" sideOffset={10}>
            Prompt
          </TooltipContent>
        </Tooltip>
        {!isLiveMode && <Checklist />}
        {!isLiveMode && (
          <Button
            variant="outline"
            className="[&_svg]:size-5"
            onClick={() => setShowDeployModal(true)}
          >
            <Rocket className="text-primary-accent" />
            Publish
          </Button>
        )}
        <Tabs
          defaultValue={flowMode}
          onValueChange={value => {
            setFlowMode(value as FlowMode);

            // Preserve the test data when switching to live mode
            if (value === FlowMode.LIVE) {
              setFlowData({
                ...flowData,
                test: {
                  version: flowData.test.version,
                  nodes: nodes,
                  edges: edges,
                },
              });
            }
          }}
        >
          <TabsList>
            <TabsTrigger
              value={FlowMode.LIVE}
              className="rounded cursor-pointer"
            >
              <span className="h-2.5 w-2.5 rounded-full bg-green-light border border-green mr-2" />
              Live
            </TabsTrigger>
            <TabsTrigger
              value={FlowMode.TEST}
              className="rounded cursor-pointer"
            >
              Edit Mode
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <Separator orientation="vertical" className="h-auto" />
        <AutoSave disabled={isLiveMode} />
      </div>
      {/* MODALS */}
      <DeployModal
        onPublish={handlePublish}
        showModal={showDeployModal}
        setShowModal={setShowDeployModal}
      />
      <ResetModal
        onReset={handleReset}
        showModal={showResetModal}
        setShowModal={setShowResetModal}
      />
      <SwitchModeModal
        onSwitchMode={handleSwitchMode}
        showModal={showSwitchModeModal}
        setShowModal={setShowSwitchModeModal}
        loading={savingState === SavingState.SAVING}
      />
    </div>
  );

  return TopbarContent;
};

export default Topbar;
